"use client";

import { memo, useCallback, useRef, useEffect, useMemo } from "react";
import type { ArtifactKind, UIArtifact } from "./artifact";
import { useArtifact } from "@/hooks/use-artifact";
import equal from "fast-deep-equal";
import { DocumentToolCall, DocumentToolResult } from "./document";
import { InlineDocumentSkeleton } from "./document-skeleton";
import { FullscreenIcon } from "./icons";
import {useSelector, useDispatch} from "react-redux";
import {setActiveNavItem
} from "@/store/mainSlice";
import ReactMarkdown from "react-markdown";
import { cn } from "@/lib/utils";
import { Editor } from "./text-editor";
import { ImageEditor } from "./image-editor";
import { Markdown } from "./markdown";

interface DocumentPreviewProps {
  isReadonly: boolean;
  result?: {
    url: string;
    title?: string;
    kind?: ArtifactKind;
    id?: string;
  };
  isLast: boolean;
}

export function DocumentPreview({
  isReadonly,
  result,
  isLast,
}: DocumentPreviewProps) {
  const hitboxRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { setArtifact, artifact } = useArtifact();
  const { liveWebsiteUrl } = useSelector((state) => state.main);

  if (!result || !result.url) {
    return (
      <div className="w-full h-[200px] flex items-center justify-center text-gray-400 border rounded-md">
        No preview available
      </div>
    );
  }

  const isFullscreen = !!artifact?.isVisible;
  const title = result.title || "Page Preview";
  const kind = result.kind || "html";
  const value = result.url;

  const isInlineHtml =
    typeof value === "string" && /<html|<!DOCTYPE/i.test(value);

  useEffect(() => {
    if (!iframeRef.current) return;

    iframeRef.current.src = "";

    if (isInlineHtml) {
      const modifiedHtml = value.replace(
        "</head>",
        `<style>
     a {
       pointer-events: none !important;
       cursor: default !important;
       text-decoration: none !important;
       color: inherit !important;
     }
   </style></head>`
);

      const blob = new Blob([modifiedHtml], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      iframeRef.current.src = url;

      return () => {
        URL.revokeObjectURL(url); // cleanup
      };
    } else {
      iframeRef.current.src = `${value}?t=${Date.now()}`;
    }
  }, [value, isInlineHtml, liveWebsiteUrl]);

  // 🟢 Special case: if kind is "text" or "document", use DocumentContent
  if (kind === "text" || kind === "document") {
    const document = useMemo(
      () => ({
        title: title,
        kind: kind,
        content: value,
        id: result.id,
        createdAt: new Date(),
        userId: "noop",
      }),
      [title, kind, value, result.id]
    );

    return (
      <div 
      // className="relative w-full border dark:border-0 rounded-lg overflow-hidden mb-10"
      className={`relative w-full border dark:border-0 rounded-lg rounded-[12px] mb-10 overflow-hidden ${
        isFullscreen ? "hidden" : "block"
      }`}
      >
        {isLast && (
          <DocumentHeader
            title={document.title}
            kind={document.kind}
            isStreaming={artifact.status === "streaming"}
          />
        )}
        <DocumentContent document={document} />
        {isLast && (
          <HitboxLayer
            hitboxRef={hitboxRef}
            result={result}
            setArtifact={setArtifact}
          />
        )}
      </div>
    );
  }

  // 🟡 Default case (HTML or others)
  return (
    <div
      className={`relative w-full border dark:border-0 rounded-lg rounded-[12px] overflow-hidden ${
        isFullscreen ? "hidden" : "block"
      }`}
    >
      {isLast && (
        <DocumentHeader title={title} kind={kind} isStreaming={false} />
      )}

      <div className="w-full h-[400px] border-t">
        <iframe
          data-hj-allow-iframe
          ref={iframeRef}
          className="w-full h-full document-preview-iframe"
          sandbox="allow-scripts allow-same-origin"
          title={title}
          id="document-preview-iframe"
        />
      </div>

      {isLast && (
        <HitboxLayer
          hitboxRef={hitboxRef}
          result={result}
          setArtifact={setArtifact}
        />
      )}
    </div>
  );

  // return (
  //   <div
  //     className={`relative w-full border dark:border-0 rounded-lg rounded-[12px] overflow-hidden ${
  //       isFullscreen ? "hidden" : "block"
  //     }`}
  //   >
  //     {/* Header */}
  //     {isLast && (
  //       <DocumentHeader title={title} kind={kind} isStreaming={false} />
  //     )}

  //     {/* ✅ Document or HTML Preview */}
  //     {kind === "document" || kind === "text" ? (
  //         <div className="w-full bg-white text-gray-800 p-8 leading-relaxed max-h-[400px] overflow-y-auto rounded-b-lg">
  //           <article className="prose prose-lg max-w-none">
  //             <ReactMarkdown>{value}</ReactMarkdown>
  //           </article>
  //         </div>
  //     ) : (
  //       // <div className="w-full bg-white text-gray-800 p-8 leading-relaxed max-h-[400px] overflow-y-auto rounded-b-lg">
  //       //   <article
  //       //     className="prose prose-lg max-w-none"
  //       //     dangerouslySetInnerHTML={{
  //       //       __html:
  //       //         result.url && result.url.startsWith("http")
  //       //           ? `<div class='text-gray-400 text-center py-4'>Loading document...</div>`
  //       //           : (result.url || result.text || "")
  //       //               .replace(/\n/g, "<br/>")
  //       //               .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
  //       //               .replace(
  //       //                 /## (.*?)<br\/>/g,
  //       //                 "<h2 class='text-xl font-semibold'>$1</h2>"
  //       //               )
  //       //               .replace(
  //       //                 /# (.*?)<br\/>/g,
  //       //                 "<h1 class='text-2xl font-bold'>$1</h1>"
  //       //               )
  //       //               .replace(/\* (.*?)<br\/>/g, "<li>$1</li>")
  //       //               .replace(/<\/li>\s*<li>/g, "</li><li>")
  //       //               .replace(
  //       //                 /(<li>.*<\/li>)/gs,
  //       //                 "<ul class='list-disc ml-6'>$1</ul>"
  //       //               ),
  //       //     }}
  //       //   />
  //       // </div>
  //       // HTML Preview (existing logic)
  //       <div className="w-full h-[400px] border-t">
  //         <iframe
  //           data-hj-allow-iframe
  //           ref={iframeRef}
  //           className="w-full h-full document-preview-iframe"
  //           sandbox="allow-scripts allow-same-origin"
  //           title={title}
  //           id="document-preview-iframe"
  //         />
  //       </div>
  //     )}

  //     {/* Fullscreen hitbox */}
  //     {isLast && (
  //       <HitboxLayer
  //         hitboxRef={hitboxRef}
  //         result={result}
  //         setArtifact={setArtifact}
  //       />
  //     )}
  //   </div>
  // );
}

const PureHitboxLayer = ({
  hitboxRef,
  result,
  setArtifact,
}: {
  hitboxRef: React.RefObject<HTMLDivElement>;
  result: any;
  setArtifact: (
    updaterFn: UIArtifact | ((currentArtifact: UIArtifact) => UIArtifact)
  ) => void;
}) => {
  const dispatch = useDispatch();
  const handleClick = useCallback(() => {
    const boundingBox = hitboxRef.current?.getBoundingClientRect() ?? {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    };

    // console.log(result.url,"result3232")

    setArtifact((artifact) => ({
      ...artifact,
      title: result.title ?? "Page Preview",
      documentId: result.id,
      kind: result.kind ?? "html",
      url: result.url,
      content: result.url || result.text || "",
      isVisible: true,
      isFullscreen: true,
      boundingBox: {
        left: boundingBox.x,
        top: boundingBox.y,
        width: boundingBox.width,
        height: boundingBox.height,
      },
    }));
  }, [setArtifact, result, hitboxRef]);

  return (
    <div
      ref={hitboxRef}
      className="absolute right-2 top-2 z-20"
      onClick={() => {
        dispatch(setActiveNavItem("z-[46]"));
        handleClick();
      }}
      // onClick={handleClick}
    >
      <div className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-zinc-700 cursor-pointer">
        <FullscreenIcon />
      </div>
    </div>
  );
};

const HitboxLayer = memo(PureHitboxLayer, (prevProps, nextProps) => {
  if (!equal(prevProps.result, nextProps.result)) return false;
  return true;
});

const PureDocumentHeader = ({
  title,
  kind,
  isStreaming,
}: {
  title: string;
  kind: ArtifactKind;
  isStreaming: boolean;
}) => (
  <div className="p-4 rounded-t-2xl flex flex-row gap-2 items-start sm:items-center justify-between dark:bg-muted border-b-0 dark:border-zinc-700">
    <div className="flex flex-row items-start sm:items-center gap-3">
      <div className="text-muted-foreground"></div>
      <div className="-translate-y-1 sm:translate-y-0 font-medium">{title}</div>
    </div>
    <div className="w-8" />
  </div>
);

const DocumentHeader = memo(PureDocumentHeader, (prevProps, nextProps) => {
  if (prevProps.title !== nextProps.title) return false;
  if (prevProps.isStreaming !== nextProps.isStreaming) return false;
  return true;
});

const DocumentContent = ({ document }: { document: Document }) => {
  const { artifact } = useArtifact();

  const containerClassName = cn(
    "h-[257px] overflow-y-scroll rounded-b-2xl border border-t-0 dark:border-zinc-700 dark:bg-muted",
    {
      "p-4 sm:px-14 sm:py-16": document.kind === "text",
      "p-0": document.kind === "code",
    }
  );

  const commonProps = {
    content: document.content ?? "",
    isCurrentVersion: true,
    currentVersionIndex: 0,
    status: artifact.status,
    saveContent: () => null,
    suggestions: [],
  };

  const handleSaveContent = () => null;

  return (
    <div className={containerClassName}>
      { document.kind === "text" ? (
        // <Editor {...commonProps} onSaveContent={handleSaveContent} />
        document.title === "Chat Preview" ? (
          console.log('-Documet-preview-', document.content),
    // <div className="prose max-w-none dark:prose-invert p-4">
    //   <ReactMarkdown>{document.content}</ReactMarkdown>
    // </div>
      <div className="p-4">
    <Markdown>{document.content}</Markdown>
  </div>
  ) : (
    <Editor {...commonProps} onSaveContent={handleSaveContent} />
  )
      ) : document.kind === "code" ? (
        <div className="relative flex w-full flex-1">
          <div className="absolute inset-0">
            <CodeEditor {...commonProps} onSaveContent={handleSaveContent} />
          </div>
        </div>
      ) : document.kind === "sheet" ? (
        <div className="relative flex size-full flex-1 p-4">
          <div className="absolute inset-0">
            <SpreadsheetEditor {...commonProps} />
          </div>
        </div>
      ) : document.kind === "image" ? (
        <ImageEditor
          content={document.content ?? ""}
          currentVersionIndex={0}
          isCurrentVersion={true}
          isInline={true}
          status={artifact.status}
          title={document.title}
        />
      ) : null}
    </div>
  );
};
